import { useEffect, useMemo, useRef, useState } from "react";
import { parse, type AstNode } from "@pix/script";

interface Props {
  /** ファイル名 → ソース */
  scripts: Record<string, string>;
  /** 現在開いているファイル(ハイライト用) */
  activeFile: string;
  /** ノードクリック時(対象ファイルへ移動して該当ラベルへスクロール) */
  onSelect?: (file: string, label: string) => void;
}

/**
 * 全スクリプトから「ラベル → ジャンプ/コール先」のグラフを抽出し、
 * 力学レイアウトで配置した SVG として描画する。
 */
export function SceneFlow({ scripts, activeFile, onSelect }: Props) {
  const graph = useMemo(() => buildGraph(scripts), [scripts]);
  const positions = useForceLayout(graph);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.px + (e.clientX - dragRef.current.x),
      y: dragRef.current.py + (e.clientY - dragRef.current.y),
    });
  };
  const onMouseUp = () => {
    dragRef.current = null;
  };
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const k = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.max(0.3, Math.min(3, z * k)));
  };

  useEffect(() => {
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, [activeFile]);

  if (graph.nodes.length === 0) {
    return (
      <div className="scene-flow empty">
        <div className="scene-flow-empty-note">ラベルが見つかりません</div>
      </div>
    );
  }

  const W = 1200;
  const H = 800;

  return (
    <div
      className="scene-flow"
      ref={containerRef}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
    >
      <div className="scene-flow-controls">
        <button onClick={() => setZoom((z) => Math.min(3, z * 1.2))}>＋</button>
        <button onClick={() => setZoom((z) => Math.max(0.3, z * 0.83))}>−</button>
        <button
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          ⌂
        </button>
        <span className="scene-flow-stats">
          {graph.nodes.length} ノード / {graph.edges.length} エッジ
        </span>
      </div>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: "block", cursor: dragRef.current ? "grabbing" : "grab" }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 9 3, 0 6" fill="#88aacc" />
          </marker>
          <marker
            id="arrowhead-choice"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 9 3, 0 6" fill="#ffaa66" />
          </marker>
        </defs>
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {graph.edges.map((e, i) => {
            const from = positions[e.from];
            const to = positions[e.to];
            if (!from || !to) return null;
            const stroke =
              e.kind === "jump" ? "#66aaff" : e.kind === "choice" ? "#ffaa66" : "#aa88ff";
            return (
              <line
                key={i}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={stroke}
                strokeWidth={1.2}
                strokeDasharray={e.kind === "call" ? "4 2" : undefined}
                markerEnd={
                  e.kind === "choice" ? "url(#arrowhead-choice)" : "url(#arrowhead)"
                }
                opacity={0.7}
              />
            );
          })}
          {graph.nodes.map((n) => {
            const p = positions[n.id];
            if (!p) return null;
            const isActive = n.file === activeFile;
            const label = n.label.length > 18 ? `${n.label.slice(0, 16)}…` : n.label;
            return (
              <g
                key={n.id}
                transform={`translate(${p.x},${p.y})`}
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect?.(n.file, n.label);
                }}
              >
                <rect
                  x={-60}
                  y={-14}
                  width={120}
                  height={28}
                  rx={4}
                  fill={isActive ? "#2a4570" : "#161c28"}
                  stroke={isActive ? "#66aaff" : "#3a4560"}
                  strokeWidth={1.5}
                />
                <text
                  x={0}
                  y={4}
                  textAnchor="middle"
                  fontSize="11"
                  fontFamily="monospace"
                  fill={isActive ? "#ffd28a" : "#aab2c0"}
                >
                  {label}
                </text>
                {n.file !== activeFile && (
                  <text
                    x={0}
                    y={-18}
                    textAnchor="middle"
                    fontSize="8"
                    fontFamily="monospace"
                    fill="#557799"
                  >
                    {n.file}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

// ===========================================================
// Graph extraction
// ===========================================================

interface FlowNode {
  id: string; // `${file}#${label}`
  file: string;
  label: string;
}

interface FlowEdge {
  from: string;
  to: string;
  kind: "jump" | "call" | "choice";
}

interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

function nodeId(file: string, label: string): string {
  return `${file}#${label}`;
}

function buildGraph(scripts: Record<string, string>): FlowGraph {
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const seen = new Set<string>();

  for (const [file, source] of Object.entries(scripts)) {
    let ast: AstNode[] = [];
    try {
      ast = parse(source, file).nodes;
    } catch {
      continue;
    }
    let currentLabel: string | null = null;
    walk(ast, file);
    function walk(ns: AstNode[], file: string) {
      for (const n of ns) {
        if (n.type === "Label") {
          currentLabel = n.name;
          const id = nodeId(file, currentLabel);
          if (!seen.has(id)) {
            seen.add(id);
            nodes.push({ id, file, label: currentLabel });
          }
        } else if (n.type === "Command") {
          if (n.name === "jump" || n.name === "call") {
            if (!currentLabel) continue;
            const tgt = resolveTarget(file, n.args);
            if (tgt) {
              ensureNode(nodes, seen, tgt);
              edges.push({
                from: nodeId(file, currentLabel),
                to: nodeId(tgt.file, tgt.label),
                kind: n.name,
              });
            }
          }
        } else if (n.type === "Choice") {
          if (!currentLabel) continue;
          for (const o of n.options) {
            if (!o.targetLabel) continue;
            const tgt = { file: o.targetFile ?? file, label: o.targetLabel };
            ensureNode(nodes, seen, tgt);
            edges.push({
              from: nodeId(file, currentLabel),
              to: nodeId(tgt.file, tgt.label),
              kind: "choice",
            });
          }
        } else if (n.type === "If") {
          for (const br of n.branches) walk(br.body, file);
          if (n.elseBody) walk(n.elseBody, file);
        } else if (n.type === "For") {
          walk(n.body, file);
        } else if (n.type === "Macro") {
          walk(n.body, file);
        }
      }
    }
  }
  return { nodes, edges };
}

function ensureNode(
  nodes: FlowNode[],
  seen: Set<string>,
  tgt: { file: string; label: string },
) {
  const id = nodeId(tgt.file, tgt.label);
  if (!seen.has(id)) {
    seen.add(id);
    nodes.push({ id, ...tgt });
  }
}

function resolveTarget(
  currentFile: string,
  args: Record<string, unknown>,
): { file: string; label: string } | null {
  const raw = String(args["_raw"] ?? args["target"] ?? "").trim();
  if (!raw) return null;
  // "#label" or "file#label" or "label" 形式に対応
  if (raw.startsWith("#")) {
    return { file: currentFile, label: raw.slice(1) };
  }
  const hashIdx = raw.indexOf("#");
  if (hashIdx > 0) {
    return { file: raw.slice(0, hashIdx), label: raw.slice(hashIdx + 1) };
  }
  return { file: currentFile, label: raw };
}

// ===========================================================
// Force-directed layout (極めて簡易な反復)
// ===========================================================

interface Point {
  x: number;
  y: number;
}

function useForceLayout(graph: FlowGraph): Record<string, Point> {
  return useMemo(() => layout(graph), [graph]);
}

function layout(graph: FlowGraph): Record<string, Point> {
  const positions: Record<string, Point> = {};
  const W = 1200;
  const H = 800;

  // ファイル別に縦バンドを割り当て、その中で円形配置 → 力学緩和
  const files = Array.from(new Set(graph.nodes.map((n) => n.file)));
  const filePositions: Record<string, FlowNode[]> = {};
  for (const f of files) filePositions[f] = [];
  for (const n of graph.nodes) filePositions[n.file]!.push(n);

  const fileW = W / Math.max(files.length, 1);
  files.forEach((f, fi) => {
    const list = filePositions[f]!;
    list.forEach((n, ni) => {
      const cx = fileW * (fi + 0.5);
      const cy = ((ni + 1) / (list.length + 1)) * H;
      positions[n.id] = { x: cx, y: cy };
    });
  });

  // 簡易力学緩和: エッジは引っ張る、近すぎるノードは反発
  const K_SPRING = 0.005;
  const K_REPEL = 8000;
  const TARGET = 110;
  const ITER = 80;

  for (let iter = 0; iter < ITER; iter++) {
    const force: Record<string, Point> = {};
    for (const n of graph.nodes) force[n.id] = { x: 0, y: 0 };

    // 反発
    for (let i = 0; i < graph.nodes.length; i++) {
      for (let j = i + 1; j < graph.nodes.length; j++) {
        const a = graph.nodes[i]!;
        const b = graph.nodes[j]!;
        const pa = positions[a.id]!;
        const pb = positions[b.id]!;
        const dx = pa.x - pb.x;
        const dy = pa.y - pb.y;
        const d2 = dx * dx + dy * dy + 1;
        const d = Math.sqrt(d2);
        const f = K_REPEL / d2;
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        force[a.id]!.x += fx;
        force[a.id]!.y += fy;
        force[b.id]!.x -= fx;
        force[b.id]!.y -= fy;
      }
    }

    // 引力 (エッジ)
    for (const e of graph.edges) {
      const pa = positions[e.from];
      const pb = positions[e.to];
      if (!pa || !pb) continue;
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const d = Math.sqrt(dx * dx + dy * dy) + 1;
      const f = (d - TARGET) * K_SPRING;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      force[e.from]!.x += fx;
      force[e.from]!.y += fy;
      force[e.to]!.x -= fx;
      force[e.to]!.y -= fy;
    }

    // 適用 + クランプ
    for (const n of graph.nodes) {
      const p = positions[n.id]!;
      const f = force[n.id]!;
      const dx = Math.max(-20, Math.min(20, f.x));
      const dy = Math.max(-20, Math.min(20, f.y));
      p.x = Math.max(70, Math.min(W - 70, p.x + dx));
      p.y = Math.max(30, Math.min(H - 30, p.y + dy));
    }
  }

  return positions;
}
